'use client';

import { useEffect, useState, useCallback } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { tmdb } from '@/lib/tmdb';
import { Header } from '@/components/Header';
import { MediaCard } from '@/components/MediaCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp,
  Film,
  Tv,
  Star,
  Calendar,
  Sparkles,
  ArrowLeft,
  Rocket,
  Building2,
  Crown,
  Swords,
  Theater
} from 'lucide-react';

interface MediaItem {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  vote_average: number;
  overview?: string;
  release_date?: string;
  first_air_date?: string;
  media_type?: 'tv' | 'movie';
  genre_ids?: number[];
}

const GENRE_MAP: Record<string, { id: number; name: string; icon: any; color: string }> = {
  'action': { id: 28, name: 'Action', icon: Star, color: 'border-orange-500/50 text-orange-400' },
  'drama': { id: 18, name: 'Drama', icon: Film, color: 'border-purple-500/50 text-purple-400' },
  'comedy': { id: 35, name: 'Comedy', icon: Sparkles, color: 'border-yellow-500/50 text-yellow-400' },
  'horror': { id: 27, name: 'Horror', icon: Film, color: 'border-red-500/50 text-red-400' },
  'romance': { id: 10749, name: 'Romance', icon: Film, color: 'border-pink-500/50 text-pink-400' },
  'thriller': { id: 53, name: 'Thriller', icon: Film, color: 'border-gray-500/50 text-gray-400' },
  'animation': { id: 16, name: 'Animation', icon: Sparkles, color: 'border-green-500/50 text-green-400' },
  'anime': { id: 16, name: 'Anime', icon: Tv, color: 'border-red-500/50 text-red-400' },
  'scifi': { id: 878, name: 'Sci-Fi & Fantasy', icon: Rocket, color: 'border-blue-500/50 text-blue-400' },
  'documentary': { id: 99, name: 'Documentary', icon: Building2, color: 'border-teal-500/50 text-teal-400' },
  'family': { id: 10751, name: 'Family', icon: Crown, color: 'border-pink-500/50 text-pink-400' },
  'war': { id: 10752, name: 'War & History', icon: Swords, color: 'border-amber-500/50 text-amber-400' },
  'western': { id: 37, name: 'Western', icon: Theater, color: 'border-yellow-700/50 text-yellow-600' },
  'trending': { id: 0, name: 'Trending', icon: TrendingUp, color: 'border-red-500/50 text-red-400' },
  'popular-movies': { id: 0, name: 'Popular Movies', icon: Film, color: 'border-red-500/50 text-red-400' },
  'popular-shows': { id: 0, name: 'Popular TV Shows', icon: Tv, color: 'border-green-500/50 text-green-400' },
  'top-rated': { id: 0, name: 'Top Rated', icon: Star, color: 'border-yellow-500/50 text-yellow-400' },
  'top-rated-shows': { id: 0, name: 'Top Rated TV Shows', icon: Tv, color: 'border-yellow-500/50 text-yellow-400' },
  'top-rated-movies': { id: 0, name: 'Top Rated Movies', icon: Film, color: 'border-yellow-500/50 text-yellow-400' },
  'upcoming': { id: 0, name: 'Coming Soon', icon: Calendar, color: 'border-blue-500/50 text-blue-400' },
  'recently-released': { id: 0, name: 'Recently Released', icon: Film, color: 'border-green-500/50 text-green-400' },
};

export default function BrowseCategoryPage() {
  const params = useParams();
  const router = useRouter();
  const category = params.category as string;

  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const categoryConfig = GENRE_MAP[category];

  const fetchContent = useCallback(async (pageNum: number = 1, reset: boolean = true) => {
    if (!categoryConfig) return;

    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let data;

      switch (category) {
        case 'trending':
          const [trendingShows, trendingMovies] = await Promise.all([
            tmdb.getTrendingShows('week', pageNum),
            tmdb.getTrendingMovies('week', pageNum)
          ]);
          data = {
            results: [
              ...trendingShows.results.map((item: any) => ({ ...item, media_type: 'tv' })),
              ...trendingMovies.results.map((item: any) => ({ ...item, media_type: 'movie' }))
            ].sort((a, b) => b.vote_average - a.vote_average)
          };
          break;

        case 'popular-movies':
          data = await tmdb.getPopularMovies(pageNum);
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'movie' }));
          break;

        case 'popular-shows':
          data = await tmdb.getPopularShows(pageNum);
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'tv' }));
          break;

        case 'top-rated':
          data = await tmdb.getTopRatedShows(pageNum);
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'tv' }));
          break;

        case 'upcoming':
          // Upcoming movies API now only returns future releases
          data = await tmdb.getUpcomingMovies(pageNum);
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'movie' }));
          break;

        case 'recently-released':
          data = await tmdb.getRecentlyReleasedMovies(pageNum);
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'movie' }));
          break;

        case 'top-rated-shows':
          data = await tmdb.getTopRatedShows(pageNum);
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'tv' }));
          break;

        case 'top-rated-movies':
          data = await tmdb.getTopRatedMovies(pageNum);
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'movie' }));
          break;

        case 'anime':
          // For anime, we'll get animation genre and filter for Japanese content
          data = await tmdb.discoverShows({
            with_genres: '16',
            with_original_language: 'ja',
            page: pageNum
          });
          data.results = data.results.map((item: any) => ({ ...item, media_type: 'tv' }));
          break;

        default:
          // For other genres, get both movies and TV shows
          const [movies, shows] = await Promise.all([
            tmdb.discoverMovies({ with_genres: categoryConfig.id.toString(), page: pageNum }),
            tmdb.discoverShows({ with_genres: categoryConfig.id.toString(), page: pageNum })
          ]);
          data = {
            results: [
              ...movies.results.map((item: any) => ({ ...item, media_type: 'movie' })),
              ...shows.results.map((item: any) => ({ ...item, media_type: 'tv' }))
            ].sort((a, b) => b.vote_average - a.vote_average)
          };
          break;
      }

      if (reset) {
        setItems(data.results);
      } else {
        // Deduplicate items by id and media_type to prevent duplicates
        setItems(prev => {
          const combined = [...prev, ...data.results];
          return combined.filter((item, index, self) =>
            index === self.findIndex(t => t.id === item.id && t.media_type === item.media_type)
          );
        });
      }

      setHasMore(data.results.length === 20);
      setPage(pageNum);
    } catch (error) {
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category, categoryConfig]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchContent(page + 1, false);
    }
  }, [fetchContent, page, loadingMore, hasMore]);

  useEffect(() => {
    if (categoryConfig) {
      fetchContent(1, true);
    }
  }, [category, fetchContent, categoryConfig]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        >= document.documentElement.offsetHeight - 1000
      ) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMore]);

  if (!categoryConfig) {
    return (
      <div className="min-h-screen bg-black">
        <Header showSearch={false} transparent={true} page="detail" />
        <div className="pt-28 sm:pt-28 md:pt-32 container mx-auto px-4">
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold text-white mb-4">Category Not Found</h1>
            <Button onClick={() => router.push('/')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const IconComponent = categoryConfig.icon;

  return (
    <div className="min-h-screen bg-black">
      <Header showSearch={false} transparent={true} page="detail" />

      <div className="pt-28 sm:pt-28 md:pt-32 container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header Section */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center gap-3 sm:gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => router.push('/')}
              className="text-gray-400 hover:text-white hover:bg-white/10 p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-red-600/20 to-red-800/20 border border-red-500/30 flex items-center justify-center backdrop-blur-sm">
              <IconComponent className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                {categoryConfig.name}
              </h1>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[2/3] w-full rounded-xl bg-gray-800" />
                <Skeleton className="h-4 w-full bg-gray-800" />
                <Skeleton className="h-3 w-3/4 bg-gray-800" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
              {items.filter(item => item.poster_path && item.poster_path.trim() !== '').map((item) => (
                <div key={`${item.id}-${item.media_type}`} className="w-full">
                  <MediaCard
                    item={item}
                    mediaType={item.media_type}
                    showAddButton={true}
                    size="medium"
                    className="!w-full"
                  />
                </div>
              ))}
            </div>

            {/* Load More Indicator */}
            {loadingMore && (
              <div className="flex justify-center py-8">
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500 border-t-transparent"></div>
                  <span>Loading more content...</span>
                </div>
              </div>
            )}

            {/* No more content */}
            {!hasMore && items.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-400">You've reached the end! 🎬</p>
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}